/**
 * ChatContainer -- main chat interface rendering UI components.
 *
 * Accepts chat state as props (lifted from useChat in AppLayout)
 * so that tool_end events can propagate to the sidebar.
 *
 * Features:
 * - Auto-scroll follows new messages and streaming
 * - Stops auto-scrolling if user scrolls up (>100px from bottom)
 * - Typing indicator while waiting for first token
 * - Error display above input
 * - Empty state when no messages
 */

import { useRef, useEffect, useCallback } from "react";
import type { ChatMessage } from "../../hooks/use-chat";
import { MessageBubble } from "./message-bubble";
import { TypingIndicator } from "./typing-indicator";
import { ChatInput } from "./chat-input";
import { SuggestionChips } from "./suggestion-chips";

interface ChatContainerProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  isOnboarding?: boolean;
}

export function ChatContainer({ messages, isStreaming, error, sendMessage, clearChat, isOnboarding }: ChatContainerProps): React.ReactElement {

  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);

  // Detect if user has scrolled up away from bottom
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    userScrolledUp.current = scrollHeight - scrollTop - clientHeight > 100;
  }, []);

  // Auto-scroll on new messages or streaming updates
  useEffect(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({
        // "auto" (instant) during streaming for performance
        // "smooth" for discrete new messages
        behavior: isStreaming ? "auto" : "smooth",
      });
    }
  }, [messages, isStreaming]);

  // Check if we should show typing indicator:
  // streaming AND the last assistant message still has empty content
  const showTypingIndicator =
    isStreaming &&
    messages.length > 0 &&
    messages[messages.length - 1].role === "assistant" &&
    messages[messages.length - 1].content === "";

  return (
    <div className="flex flex-col h-full">
      {/* Header with clear button */}
      {messages.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <span className="text-xs text-text-tertiary">{messages.length} messages</span>
          <button
            type="button"
            onClick={clearChat}
            disabled={isStreaming}
            className="text-xs text-text-tertiary hover:text-text-primary disabled:opacity-50"
          >
            Clear chat
          </button>
        </div>
      )}

      {/* Message area */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="w-12 h-12 rounded-full bg-primary-subtle flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
            </div>
            <p className="text-text-tertiary text-sm">
              Start a conversation with Atlas
            </p>
            <SuggestionChips onSend={sendMessage} isOnboarding={isOnboarding ?? false} />
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
            {showTypingIndicator && <TypingIndicator />}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Error display */}
      {error && (
        <div className="alert-error mx-4 mb-2">
          {error}
        </div>
      )}

      {/* Input area */}
      <ChatInput onSend={sendMessage} disabled={isStreaming} />
    </div>
  );
}
